/**
 * Stamps `dir="auto"` onto rich-text HTML before it is injected into the page.
 *
 * Content authored in the admin/instructor Quill editor is direction-neutral
 * (the editor's only direction control adds RTL; untouched text carries no
 * direction markup), while this document is RTL whenever the UI language is
 * Arabic. Injected as-is, the bidi algorithm re-orders neutral edge characters
 * of English content — "walk to school ?" renders as "? walk to school" — and
 * flips "1."-style numbering. Per-block `dir="auto"` lets each paragraph/list
 * resolve direction from its own first strong character, so mixed Arabic and
 * English content each lay out correctly.
 *
 * Blocks the author explicitly directed (a `dir` attribute, an inline
 * `direction` style, or the legacy `ql-direction-rtl` class) are left alone.
 * Legacy content with loose text or inline elements at the top level cannot be
 * stamped per block; it is wrapped in a single `<div dir="auto">`, resolving
 * one direction for the whole value.
 */

const BLOCK_TAGS = new Set(['P', 'OL', 'UL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'DIV', 'TABLE', 'FIGURE'])

const hasExplicitDirection = (el: Element) =>
	el.hasAttribute('dir') || el.classList.contains('ql-direction-rtl') || /direction\s*:/i.test(el.getAttribute('style') ?? '')

const stampAutoDir = (html: string): string => {
	if (!html || typeof DOMParser === 'undefined') return html
	let body: HTMLElement
	try {
		body = new DOMParser().parseFromString(html, 'text/html').body
	} catch {
		return html
	}
	if (!body) return html

	const nodes = Array.from(body.childNodes)
	const hasLooseContent = nodes.some(
		node =>
			(node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== '') ||
			(node.nodeType === Node.ELEMENT_NODE && !BLOCK_TAGS.has((node as Element).tagName))
	)

	if (hasLooseContent) {
		const wrapper = body.ownerDocument.createElement('div')
		wrapper.setAttribute('dir', 'auto')
		while (body.firstChild) wrapper.appendChild(body.firstChild)
		body.appendChild(wrapper)
	} else {
		nodes.forEach(node => {
			if (node.nodeType !== Node.ELEMENT_NODE) return
			const el = node as Element
			if (!hasExplicitDirection(el)) el.setAttribute('dir', 'auto')
		})
	}
	return body.innerHTML
}

export default stampAutoDir
